import { useModalState } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { useAppStateContext } from "@src/appV2/App/useAppState";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useCreateLiveActivityForUpcomingShift } from "@src/appV2/LiveActivity/useCreateLiveActivityForUpcomingShift";
import { EnforceLocationPermissionsContainer } from "@src/appV2/LocationPermission/EnforceLocationPermissionsContainer";
import { WorkerGeolocationMissingBottomSheetContainer } from "@src/appV2/redesign/GeoLocation/WorkerGeolocationMissingBottomSheetContainer";
import { useInHomeVisibility } from "@src/appV2/redesign/InHome/lib/useInHomeVisibility";
import { ReferralActiveBottomSheet } from "@src/appV2/Referral/RedeemCode/ReferralActiveBottomSheet";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { isWorkerInviteRequired, isWorkerRestricted } from "../../ClipboardScore/utils/utils";
import { invalidateShifts } from "../../invalidateShifts";
import { MissedShifts } from "../../Shift/MissedShift/MissedShifts";
import { OpenShiftListDataProvider } from "../../Shift/Open/useOpenShiftListDataContext";
import { VirtualShiftListContextProvider } from "../../Shift/Open/useVirtualShiftListContext/useVirtualShiftListContext";
import { ShiftBoostDetailsBottomSheet } from "../../Shift/ShiftBoost/DetailsBottomSheet";
import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { ClearFiltersButton } from "../Filters/ClearFiltersButton";
import { InviteRequiredPage } from "../InviteRequired/Page";
import { ShiftDiscoveryPageHeader } from "../PageHeader";
import { RestrictedPage } from "../Restricted/Page";
import { ShiftDiscoverySearchModeSwitcher } from "../SearchModeSwitcher";
import { ListViewPageOpenShiftsList } from "./List";
import { PromoBanner } from "./PromoBanner";
import { PullToRefresh } from "./PullToRefresh";
import { ReactivationBonusBottomSheet } from "./ReactivationBonusBottomSheet";
import { useFilteredListViewOpenShiftsData } from "./useFilteredListViewOpenShiftsData";

export function ShiftDiscoveryListViewPage() {
  const queryClient = useQueryClient();
  const reactivationBonusModalState = useModalState("closed");
  const shiftBoostDetailsModalState = useModalState("closed");

  const reactivationBonusBanner = useCbhFlag(CbhFeatureFlag.REACTIVATION_BONUS_BANNER, {
    defaultValue: false,
  });

  const {
    data: {
      filteredOpenShifts,
      unfilteredOpenShifts,
      workplacesMap,
      rateNegotiationsMap,
      shiftAuctionsMap,
      offersMap,
      bundlesByShiftId,
      priorityShiftsCount,
      urgentShiftsCount,
      blockShiftsCount,
      timeSlotFilters,
    },
    isLoading: openShiftsIsLoading,
    isSuccess: openShiftsIsSuccess,
    isError: openShiftsIsError,
    pagination,
    daysCount,
  } = useFilteredListViewOpenShiftsData();

  const { inHomeCount } = useInHomeVisibility();

  const scrollRef = useRef<HTMLDivElement>(null);

  useScrollToTopOnStatusTap(scrollRef);

  const {
    state: { isAppActive },
  } = useAppStateContext();

  useCreateLiveActivityForUpcomingShift();

  // This event is used for analytics for count of shoppers. The legacy event we were relying on was being sent on every app reopening, this is why we are adding the app state check.
  useLogEffect(
    APP_V2_USER_EVENTS.OPEN_SHIFTS_LIST_VIEWED,
    { shiftsReturned: unfilteredOpenShifts.length },
    { enabled: isAppActive && openShiftsIsSuccess }
  );

  const worker = useDefinedWorker();
  const isInviteRequired = isWorkerInviteRequired(worker);
  const isRestricted = isWorkerRestricted(worker);

  if (isInviteRequired) {
    return <InviteRequiredPage />;
  }

  if (isRestricted) {
    return <RestrictedPage />;
  }

  return (
    <Stack
      sx={(theme) => ({
        flex: 1,
        backgroundColor: theme.palette.background.tertiary,
        overflow: "hidden",
        position: "relative",
      })}
    >
      <EnforceLocationPermissionsContainer />
      <OpenShiftListDataProvider
        openShifts={filteredOpenShifts}
        workplacesMap={workplacesMap}
        rateNegotiationsMap={rateNegotiationsMap}
        shiftAuctionsMap={shiftAuctionsMap}
        offersMap={offersMap}
        bundlesByShiftId={bundlesByShiftId}
        isOpenShiftsLoading={openShiftsIsLoading}
        isOpenShiftsError={openShiftsIsError}
        isOpenShiftsSuccess={openShiftsIsSuccess}
        canLoadMoreShifts={pagination?.hasNextPage ?? false}
        isLoadingMoreShifts={pagination?.isFetchingNextPage ?? false}
        loadMoreShifts={() => {
          void pagination?.fetchNextPage();
        }}
      >
        <VirtualShiftListContextProvider
          timeSlotFilters={timeSlotFilters}
          shifts={filteredOpenShifts}
          workplacesMap={workplacesMap}
        >
          <ShiftDiscoveryPageHeader
            priorityShiftsCount={priorityShiftsCount}
            urgentShiftsCount={urgentShiftsCount}
            blockShiftsCount={blockShiftsCount}
            inHomeCount={inHomeCount}
          />

          <PullToRefresh
            scrollRef={scrollRef}
            onRefresh={async () => {
              await invalidateShifts(queryClient);
            }}
          />
          <MissedShifts />

          {reactivationBonusBanner && (
            <PromoBanner
              message="We missed you! Get a $50 bonus when you complete your next shift."
              actionText="Learn More"
              onAction={reactivationBonusModalState.openModal}
            />
          )}

          <ListViewPageOpenShiftsList daysCount={daysCount} customScrollParentRef={scrollRef} />

          <ShiftDiscoverySearchModeSwitcher>
            <ClearFiltersButton allOpenShiftsCount={unfilteredOpenShifts.length} />
          </ShiftDiscoverySearchModeSwitcher>

          <ReactivationBonusBottomSheet modalState={reactivationBonusModalState} />
          <ShiftBoostDetailsBottomSheet modalState={shiftBoostDetailsModalState} />
          <ReferralActiveBottomSheet />
          <WorkerGeolocationMissingBottomSheetContainer />
        </VirtualShiftListContextProvider>
      </OpenShiftListDataProvider>
    </Stack>
  );
}
